import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Select, Button, Input, InputNumber, Divider, Radio, Dropdown, Spin, Menu, Modal, Tooltip, Popover } from 'antd';
import style from './CrashSightLeftMenu.scss';
import { useTranslation } from 'react-i18next';
// import dashboardLeftLogo from 'images/v2/newcs_dashboard_left_logo.png';
import CsLogo from 'svg/v2/newcs_dashboard_textlogo2.svg';
import { MdOutlineDashboard, MdLegendToggle, MdTroubleshoot, MdConstruction, MdOutlineSettings, MdOutlineDashboardCustomize, MdOutlineToken } from 'react-icons/md';
import CrashIconNormal from 'svg/v2/newcs_dashboard_left_crash_icon_normal.svg';
import AnrIconNormal from 'svg/v2/newcs_dashboard_left_anr_icon_normal.svg';
import ErrorIconNormal from 'svg/v2/newcs_dashboard_left_error_icon_normal.svg';
import OomIconNormal from 'svg/v2/newcs_dashboard_left_oom_icon_normal.svg';
import JankIconNormal from 'svg/v2/newcs_dashboard_left_jank_icon_normal.svg';
import HangIconNormal from 'svg/v2/newcs_dashboard_left_hang_icon_normal.svg';
import TutorialIconNormal from 'svg/v2/newcs_dashboard_left_tutorial_icon_normal.svg';
import PackupArrowNormal from 'svg/v2/newcs_dashboard_left_packup_arrow_normal.svg';
import InsightAnalysisIcon from 'svg/v2/newcs_dashboard_left_statisticalanalysis_icon_normal.svg'
import ChatBotIcon from 'svg/v2/newcs_dashboard_topbar_chatbot_icon_normal_menu.svg';

import { isAndroid, isAndroidOrHarmony, isGameConsole, isIos, isMobile, isSwitch, PlatformUtil } from 'utils/platform';
import {
  selectCurrentAppIsNetmarble,
  selectFrontendWebVolatileConfigValueByKey,
  selectHasSpecifiedPermissionInCurrentApp,
} from 'utils/selectors/selectors';
import { connect, useSelector } from 'react-redux';
import {
  FrontendWebVolatileConfigKeys,
} from 'utils/constants/frontend-web-volatile-config';
import { PreferencesSubMenuUtil } from 'components/top/PreferencesSubMenu';
import { ServerAppSettings } from 'utils/server-app-settings';
import { isThreadMonitorMenuEnabled } from 'utils/thread-monitor-url';
import { UserTypeEnum } from 'utils/constants/user-type-enum';
import { ze } from 'utils/zhEn';
import { EnvUtil } from 'utils/env-util';
import { useSize } from 'ahooks';
import { MenuItem } from './MenuItem';
import { CS_STYLES } from 'utils/constants/style-constants';
import { SubAppType } from 'utils/constants/sub-app-type';
import { ExceptionTypeName } from 'utils/constants/exception-type-int';
import { BillingTeamType } from 'utils/constants/billing';
import { ix } from 'utils/i18n-with-context';
import {RolePermission} from 'utils/constants/role-permission';

const AUTO_COLLAPSE_ENABLE_WIDTH = 1500;
const AUTO_COLLAPSE_DISABLE_WIDTH = 1600;

const CrashSightLeftMenu = ({
  reduxState,
  pathname,
  search,
  currentApp,
  manuallyCollapsed,
  autoCollapsed,
  onChangeManuallyCollapsed,
  onChangeAutoCollapsed,
  hovered,
  selectFrontendWebVolatileConfigValueByKey,
  isGlobalNotification,
  localStorage,
}) => {
  const { t } = useTranslation();

  const { width: bodyWidth } = useSize(document.body) || {};
  useEffect(() => {
    if (bodyWidth < AUTO_COLLAPSE_ENABLE_WIDTH && !autoCollapsed) {
      onChangeAutoCollapsed(true);
    } else if (bodyWidth > AUTO_COLLAPSE_DISABLE_WIDTH && autoCollapsed) {
      onChangeAutoCollapsed(false);
    }
  }, [bodyWidth]);
  const collapsed = manuallyCollapsed || autoCollapsed;

  const currentAppJs = currentApp.toJS();
  const {
    appId,
    platformId: pid,
    appName,
    demoApp,
    accountId,
    subAppType,
    teamType,
  } = currentAppJs;
  const isNetmarble = useSelector(selectCurrentAppIsNetmarble); // 临时对netmarble账户下的所有项目，禁止效率工具

  // 为防止正常用户发生图标闪烁不好看，默认允许，加载完权限之后发现没有权限再禁用
  const allowViewDashboard = useSelector(state => selectHasSpecifiedPermissionInCurrentApp(state, RolePermission.VIEW_DASHBOARD, { defaultValueBeforeLoaded: true }));

  const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};

  const enableOom = serverAppSettings[ServerAppSettings.keys.enableOom];
  const enableJank = serverAppSettings[ServerAppSettings.keys.enableJank];
  const enableHang = !!serverAppSettings[ServerAppSettings.keys.enableHang];
  const enableMetricKit = !!serverAppSettings[ServerAppSettings.keys.enableMetricKit];
  const enableThreadMonitor = isThreadMonitorMenuEnabled(serverAppSettings);
  const enableApplicationExit = !!serverAppSettings[ServerAppSettings.keys.enableApplicationExit];
  const enableFeatureAnalysis = serverAppSettings[ServerAppSettings.keys.enableFeatureAnalysis];
  const enableInsightPage = !!serverAppSettings[ServerAppSettings.keys.enableInsightPage] || teamType !== BillingTeamType.COMMERCIAL || EnvUtil.isChinaEnv(); // 国内环境所有项目、以及海外的内部项目全部开放洞察页面
  const enableEfficiencyTools = teamType !== BillingTeamType.COMMERCIAL; // 只对内部项目开放效率工具
  const enableIssueStatusReopened = serverAppSettings[ServerAppSettings.keys.enableIssueStatusReopened];

  const currentUser = reduxState.user.get('current').toJS();
  const isSuper = !!currentUser.isSuper;
  // const enableAiCenter = isSuper;//超管显示AI中心侧边栏
  // const isCompareTrendVisible = !!serverAppSettings[ServerAppSettings.keys.enableAdvancedTrend];

  const isWangZhe = teamType === BillingTeamType.TENCENT && appName?.startsWith('王者');

  const isStatsViewer = currentUser.type === UserTypeEnum.STATS_VIEWER;

  const isCustomFeatureAvailable = true; // 特征分析现在对所有项目开放

  const oomAppNameWhitelist = (selectFrontendWebVolatileConfigValueByKey(FrontendWebVolatileConfigKeys.OOM_APP_NAME_WHITELIST) || '')
    .split(',')
    .map(x => x.trim())
    .filter(x => x);
  const isOomAvailable = enableOom || (isMobile(pid) && oomAppNameWhitelist.some(x => (appName || '').includes(x)));

  const issueListInitialQueryParams = {
    pid,
    status: enableIssueStatusReopened ? '0,2,4' : '0,2',
    // 王者各异常大类 issue 存在同一套索引，侧栏进入默认只看最近7天
    ...(isWangZhe ? { date: 'last_7_day' } : {}),
  };
  const jankIssueListInitialQueryParams = {
    ...issueListInitialQueryParams,
    exceptionTypeList: ExceptionTypeName.JankRaw,
  };
  const excAnalysisCommonSearch = `?${new URLSearchParams(issueListInitialQueryParams).toString()}`;
  const jankExcAnalysisCommonSearch = `?${new URLSearchParams(jankIssueListInitialQueryParams).toString()}`;
  // 王者体验服 ThreadMonitor 子菜单专用过滤参数（appId=526b64a7af，exceptionTypeList=JankV2）
  const threadMonitorSearch = `?${new URLSearchParams({ ...issueListInitialQueryParams, exceptionTypeList: ExceptionTypeName.JankV2 }).toString()}`;

  const hasAdminPermission = useSelector(state => selectHasSpecifiedPermissionInCurrentApp(state, RolePermission.EDIT));
  const preferencesLinkTo = useMemo(() => PreferencesSubMenuUtil.makeMenuItems({ currentAppJs, serverAppSettings, currentUserJs: currentUser, hasAdminPermission })[0].linkTo, [appId, pid, serverAppSettings, reduxState.user, hasAdminPermission]);

  // 将右侧滚动条的位置置顶
  const handleScrollToTop = () => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    if (scrollTop > 0) {
      window.scrollTo(0, 0);
    }
  };

  let tutorialSubAppType = undefined;
  if (subAppType === SubAppType.UNITY) {
    tutorialSubAppType = 'UNITY';
  } else if (subAppType === SubAppType.UE4 || subAppType === SubAppType.UE5) {
    tutorialSubAppType = 'UNREAL';
  }

  const menuItems = [{
    available: true,
    label: t('EXCP_LEFTMENU.overview'),
    labelAbridge: ze('概览', 'Overview'),
    pathPrefixes: ['/crash-reporting/dashboard'],
    linkTo: { pathname: `/crash-reporting/dashboard/${appId}`, search: `?pid=${pid}` },
    svg: <MdOutlineDashboard style={{ width: '24px', height: '24px' }} />,
    isReactIcons: true,
  }, {
    available: true,
    disabled: !allowViewDashboard,
    label: t('EXCP_LEFTMENU.versionDashboard'),
    labelAbridge: ze('趋势', 'Trend'),
    pathPrefixes: ['/crash-reporting/version-dashboard'],
    linkTo: { pathname: `/crash-reporting/version-dashboard/${appId}`, search: `?pid=${pid}` },
    svg: <MdLegendToggle style={{ width: '28px', height: '28px' }} />,
    isReactIcons: true,
  }, {
    available: PlatformUtil.isCrashAvailable(pid),
    label: t('EXCP_LEFTMENU.crashAnalysis'),
    labelAbridge: ze('崩溃', 'Crash'),
    pathPrefixes: ['/crash-reporting/crashes'],
    linkTo: { pathname: `/crash-reporting/crashes/${appId}`, search: excAnalysisCommonSearch },
    svg: <CrashIconNormal width="20" height="20" viewBox="0 0 20 20"/>,
  }, {
    available: PlatformUtil.isAnrAvailable(pid),
    label: t('EXCP_LEFTMENU.anrAnalysis'),
    labelAbridge: t('common.anr'),
    pathPrefixes: ['/crash-reporting/blocks'],
    linkTo: { pathname: `/crash-reporting/blocks/${appId}`, search: excAnalysisCommonSearch },
    svg: <AnrIconNormal width="20" height="20" viewBox="0 0 20 20"/>,
  }, {
    available: true,
    label: t('EXCP_LEFTMENU.errorAnalysis'),
    labelAbridge: ze('错误', 'Error'),
    pathPrefixes: ['/crash-reporting/errors'],
    linkTo: { pathname: `/crash-reporting/errors/${appId}`, search: excAnalysisCommonSearch },
    svg: <ErrorIconNormal width="20" height="20" viewBox="0 0 20 20" />,
    // enableThreadMonitor：错误下 ThreadMonitor 子菜单（复用 /errors + exceptionTypeList=JankV2）
    ...(enableThreadMonitor ? {
      children: [
        {
          available: true,
          label: ze('全部错误', 'All Errors'),
          labelAbridge: ze('全部', 'All'),
          pathPrefixes: ['/crash-reporting/errors'],
          isChild: true,
          linkTo: { pathname: `/crash-reporting/errors/${appId}`, search: excAnalysisCommonSearch },
          isActive: (pname, srch) => pname.includes('/crash-reporting/errors') && !new URLSearchParams(srch).get('exceptionTypeList')?.includes('JankV2'),
        },
        {
          available: true,
          label: 'ThreadMonitor',
          labelAbridge: 'ThreadMonitor',
          pathPrefixes: ['/crash-reporting/errors'],
          isChild: true,
          linkTo: { pathname: `/crash-reporting/errors/${appId}`, search: threadMonitorSearch },
          isActive: (pname, srch) => pname.includes('/crash-reporting/errors') && new URLSearchParams(srch).get('exceptionTypeList')?.includes('JankV2'),
        },
      ],
    } : {}),
  }, {
    available: isOomAvailable,
    label: ze('OOM分析', 'OOM'),
    labelAbridge: 'OOM',
    pathPrefixes: ['/crash-reporting/oom', '/crash-reporting/model-oom'],
    svg: <OomIconNormal width="20" height="20" viewBox="0 0 20 20" />,
    linkTo: { pathname: `/crash-reporting/oom/${appId}`, search: `?pid=${pid}` },
  }, {
    // MetricKit 分析：仅 iOS 项目且项目级开启 enableMetricKit 时可见；为可展开父节点
    available: isIos(pid) && enableMetricKit,
    label: ze('MetricKit分析', 'MetricKit'),
    labelAbridge: 'MetricKit',
    pathPrefixes: ['/crash-reporting/metrickit'],
    svg: <CrashIconNormal width="20" height="20" viewBox="0 0 20 20" />,
    children: [
      {
        available: true,
        label: ze('MetricKit概览', 'MetricKit Overview'),
        labelAbridge: ze('MK概览', 'Overview'),
        pathPrefixes: ['/crash-reporting/metrickit/'],
        isChild: true,
        linkTo: { pathname: `/crash-reporting/metrickit/${appId}`, search: `?pid=${pid}` },
      },
      {
        available: true,
        label: ze('MetricKit崩溃分析', 'MetricKit Crash'),
        labelAbridge: ze('MK崩溃', 'MK Crash'),
        pathPrefixes: ['/crash-reporting/metrickit-crash'],
        isChild: true,
        linkTo: { pathname: `/crash-reporting/metrickit-crash/${appId}`, search: `?pid=${pid}` },
      },
      // CPU/磁盘写入异常两类数据后续不以聚合问题方式落地，暂时移除入口；页面代码保留以便未来恢复。
      // {
      //   available: isSuper,
      //   label: ze('MetricKit CPU异常分析', 'MetricKit CPU Exception'),
      //   labelAbridge: ze('MK CPU', 'MK CPU'),
      //   pathPrefixes: ['/crash-reporting/metrickit-cpu'],
      //   isChild: true,
      //   linkTo: { pathname: `/crash-reporting/metrickit-cpu/${appId}`, search: `?pid=${pid}` },
      // },
      // {
      //   available: isSuper,
      //   label: ze('MetricKit磁盘写入异常分析', 'MetricKit Disk Write Exception'),
      //   labelAbridge: ze('MK磁盘', 'MK Disk'),
      //   pathPrefixes: ['/crash-reporting/metrickit-disk'],
      //   isChild: true,
      //   linkTo: { pathname: `/crash-reporting/metrickit-disk/${appId}`, search: `?pid=${pid}` },
      // },
    ],
  }, {
    available: isAndroid(pid) && enableApplicationExit,
    label: ze('应用退出信息', 'ApplicationExitInfo'),
    labelAbridge: ze('退出信息', 'AEI'),
    pathPrefixes: ['/crash-reporting/aexit', '/crash-reporting/aexit-crash', '/crash-reporting/aexit-anr'],
    svg: <CrashIconNormal width="20" height="20" viewBox="0 0 20 20" />,
    children: [
      {
        available: true,
        label: ze('应用退出信息概览', 'ApplicationExitInfo Overview'),
        labelAbridge: ze('退出信息概览', 'Overview'),
        pathPrefixes: ['/crash-reporting/aexit/'],
        isChild: true,
        linkTo: { pathname: `/crash-reporting/aexit/${appId}`, search: `?pid=${pid}` },
      },
      {
        available: true,
        label: ze('应用退出信息崩溃', 'ApplicationExitInfo Crash'),
        labelAbridge: ze('退出信息崩溃', 'AEI Crash'),
        pathPrefixes: ['/crash-reporting/aexit-crash'],
        isChild: true,
        linkTo: { pathname: `/crash-reporting/aexit-crash/${appId}`, search: `?pid=${pid}` },
      },
      {
        available: true,
        label: ze('应用退出信息ANR', 'ApplicationExitInfo ANR'),
        labelAbridge: ze('退出信息ANR', 'AEI ANR'),
        pathPrefixes: ['/crash-reporting/aexit-anr'],
        isChild: true,
        linkTo: { pathname: `/crash-reporting/aexit-anr/${appId}`, search: `?pid=${pid}` },
      },
    ],
  }, {
    available: enableJank,
    label: ze('卡顿分析', 'Jank'),
    labelAbridge: ze('卡顿', 'Jank'),
    pathPrefixes: ['/crash-reporting/janks'],
    svg: <JankIconNormal width="20" height="20" viewBox="0 0 20 20" />,
    linkTo: { pathname: `/crash-reporting/janks/${appId}`, search: jankExcAnalysisCommonSearch },
  }, {
    available: enableHang,
    label: ix(ze('卡死分析', 'Hang')),
    labelAbridge: ix(ze('卡死', 'Hang')),
    pathPrefixes: ['/crash-reporting/hangs'],
    svg: <HangIconNormal width="20" height="20" viewBox="0 0 20 20" />,
    linkTo: { pathname: `/crash-reporting/hangs/${appId}`, search: excAnalysisCommonSearch },
  }, {
    available: true,
    label: t('EXCP_LEFTMENU.search'),
    labelAbridge: ze('搜索', 'Search'),
    pathPrefixes: ['/crash-reporting/advanced-search'],
    linkTo: { pathname: `/crash-reporting/advanced-search/${appId}`, search: `?pid=${pid}`, state: { leftMenuClickTrigger: Math.random() } },
    svg: <MdTroubleshoot style={{ width: '25px', height: '25px' }} />,
    isReactIcons: true,
  },
  // {
  //   available: enableAiCenter,
  //   label:ze('AI中心','AI Center'),
  //   labelAbridge: ze('AI中心','AI Center'),
  //   pathPrefixes: [`/ai-reports`],
  //   linkTo: { pathname: `/crash-reporting/ai-reports/${appId}`, search: `?pid=${pid}`, state: { leftMenuClickTrigger: Math.random() } },
  //   svg: <ChatBotIcon />,
  // },
  {
    available: enableInsightPage,
    disabled: isStatsViewer,
    label: t('EXCP_LEFTMENU.insight'),
    labelAbridge: ze('洞察', 'Insight'),
    pathPrefixes: ['/crash-reporting/insight'],
    linkTo: { pathname: `/crash-reporting/insight/${appId}`, search: `?pid=${pid}` },
    svg: <InsightAnalysisIcon />,
  }, {
    available: isCustomFeatureAvailable,
    disabled: isStatsViewer,
    label: t('EXCP_LEFTMENU.featureAnalysis'),
    labelAbridge: ze('特征', 'Features'),
    pathPrefixes: ['/crash-reporting/feature'],
    linkTo: { pathname: `/crash-reporting/feature-stat-result/${appId}`, search: `?pid=${pid}` },
    svg: <MdOutlineDashboardCustomize style={{ width: '24px', height: '24px' }} />,
    isReactIcons: true,
  }, {
    available: appId && enableEfficiencyTools, // appId这个条件的目的是防止appInfo没加载好的时候用户能看到这个选项
    disabled: isStatsViewer,
    label: t('EXCP_LEFTMENU.EfficiencyTool'),
    labelAbridge: ze('工具', 'Tools'),
    pathPrefixes: ['/crash-reporting/online'],
    linkTo: { pathname: `/crash-reporting/online-retrace/${appId}`, search: `?pid=${pid}` },
    svg: <MdConstruction style={{ width: '24px', height: '24px' }} />,
    isReactIcons: true,
  }, {
    available: appId && enableEfficiencyTools && EnvUtil.isChinaEnv(), // Token 监控接口仅国内环境可用
    disabled: isStatsViewer,
    label: ze('Token监控', 'Token Monitor'),
    labelAbridge: ze('Token', 'Token'),
    pathPrefixes: ['/crash-reporting/token-monitor'],
    linkTo: { pathname: `/crash-reporting/token-monitor/${appId}`, search: `?pid=${pid}` },
    svg: <MdOutlineToken style={{ width: '24px', height: '24px' }} />,
    isReactIcons: true,
  }, {
    available: true,
    disabled: isStatsViewer,
    label: t('NAVBAR.setting'),
    labelAbridge: ze('设置', 'Settings'),
    pathPrefixes: ['/crash-reporting/preferences', '/product/apps/'],
    linkTo: preferencesLinkTo,
    svg: <MdOutlineSettings style={{ width: '24px', height: '24px' }} />,
    isReactIcons: true,
  // }, {
  //   available: !demoApp,
  //   disabled: isStatsViewer,
  //   label: t("NAVBAR.setting"),
  //   labelAbridge: ze('设置','Setting'),
  //   pathPrefixes: ['/product/apps/'],
  //   linkTo: { pathname: `/product/apps/${appId}`, search: `?pid=${pid}` },
  //   svg: <SettingIconNormal />,
  }, {
    available: true,
    customElement: true,
    content: <div
      className={style.divider}
      style={{ margin: collapsed ? '2px 8px 10px 8px' : '6px 20px 14px 20px' }}
    />,
  }, {
    available: true,
    label: t('EXCP_LEFTMENU.接入引导'),
    labelAbridge: ze('指南', 'Tutorial'),
    pathPrefixes: ['/crash-reporting/tutorial/'],
    linkTo: { pathname: `/crash-reporting/tutorial/${appId}`, search: tutorialSubAppType ? `?pid=${pid}&subAppType=${tutorialSubAppType}` : `?pid=${pid}` },
    svg: <TutorialIconNormal />,
  }].filter(x => x.available);

  function getMenuItemClassName(menuItem) {
    const { pathPrefixes, disabled, isReactIcons, children = [] } = menuItem;
    if (disabled) {
      return [style.menuItem, isReactIcons ? style.menuItemDisabledReactIcons : style.menuItemDisabled].join(' ');
    }
    if (children.length) {
      return pathPrefixes.some(x => (pathname || '').includes(x))
        ? [style.menuItem, style.parentMenu, style.parentMenuSelected].join(' ')
        : [style.menuItem, style.parentMenu].join(' ');
    }
    if (menuItem.isChild) {
      const isActive = menuItem.isActive
        ? menuItem.isActive(pathname || '', search || '')
        : pathPrefixes.some(x => (pathname || '').includes(x));
      return isActive
        ? [style.submenuItem, style.submenuItemSelected].join(' ')
        : [style.submenuItem].join(' ');
    }
    return pathPrefixes.some(x => (pathname || '').includes(x))
      ? [style.menuItem, style.menuItemSelected].join(' ')
      : style.menuItem;
  }

  const showCollapsed = collapsed && !hovered;

  const nowMillis = +new Date(); // 加个时间戳query参数，防止2.0用户点logo看到了缓存的1.0首页
  const logoClickHref = `/welcome?t=${nowMillis}`;


  return <div className={
    showCollapsed ? [style.crashSightLeftMenu, style.collapsed].join(' ') : style.crashSightLeftMenu
  }
  style={isGlobalNotification ? { top: `${CS_STYLES.GLOBAL_NOTIFICATION}` } : {}}
  >
    <div className={showCollapsed ? style.collapsedLogoContainer : style.logoContainer}>
      { collapsed
        ? <a href={ logoClickHref } style={{ height: '12px', display: 'flex', alignItems: 'center' }}>
           <CsLogo width="64" height="12" viewBox="0 0 120 24" style={{ color: '#393157' }}/>
        </a>
        : <a href={ logoClickHref }>
          <span style={{ display: 'block', objectFit: 'cover', marginLeft: '32px' }}>
            <CsLogo style={{ color: '#393157' }}/>
          </span></a>
      }
    </div>
    <div className={style.menuContainer}>{
      menuItems.map((x, i) => {
        if (x.customElement) {
          return <div key={i}>{x.content}</div>;
        }
        const selectedState = x.pathPrefixes.some(x => (pathname || '').includes(x));
        return <div key={x.label}>
          <MenuItem
            menuOption={x}
            showCollapsed={showCollapsed}
            getMenuItemClassName={getMenuItemClassName}
            handleScrollToTop={handleScrollToTop}
            pathname={pathname}
            search={search}
            selectedState={selectedState}
          />
        </div>;
      })
    }</div>
    { !autoCollapsed && <div
      className={style.footer}
      style={ collapsed ? {} : { justifyContent: 'end' } }
    >{
      collapsed
        ? <div
            style={{ marginLeft: '37px', transform: 'rotate(180deg)' }}
            onClick={() => onChangeManuallyCollapsed(false)}
          ><PackupArrowNormal /></div>
        : <div
            style={{ marginRight: '32px', cursor: 'pointer' }}
            onClick={() => onChangeManuallyCollapsed(true)}
          ><PackupArrowNormal /></div>
    }</div> }
  </div>;
};

CrashSightLeftMenu.props = {
  pathname: PropTypes.string.isRequired,
  currentApp: PropTypes.object.isRequired,
  manuallyCollapsed: PropTypes.bool,
  autoCollapsed: PropTypes.bool,
  onChangeManuallyCollapsed: PropTypes.func,
  onChangeAutoCollapsed: PropTypes.func,
  hovered: PropTypes.bool,
  reduxState: PropTypes.object,
};

const mapStateToProps = (state, router) => ({
  reduxState: state,
  selectFrontendWebVolatileConfigValueByKey: key => selectFrontendWebVolatileConfigValueByKey(state, key),
});


export default connect(mapStateToProps)(CrashSightLeftMenu);
